import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IndianRupee, Landmark, Package, PackageOpen, Percent, TrendingUp } from 'lucide-react';
import { AuthContext } from '../../context/AuthContext.js';
import { DashboardLayout, AmountCard, PageHeader, StatCard } from '../common/page_component/index.js';
import Loader from '../common/Loader.js';
import Alert from '../common/Alert';
import dashboardService from '../../services/DashboardService.js';
import { fullCurrency } from '../../utils/formatCurrency.js';
import ReturnBreakdownModal from './components/ReturnBreakdownModal.js';
import DateRangeFilter, { buildDateParams, DEFAULT_DATE_RANGE } from './components/DateRangeFilter.js';

const money = (value) => fullCurrency(value || 0);
const sumRows = (rows, key) => (rows || []).reduce((total, row) => total + Number(row[key] || 0), 0);

const OwnerDashboard = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState(DEFAULT_DATE_RANGE);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    if (dateRange === 'custom' && (!customStart || !customEnd)) return undefined;
    let cancelled = false;
    setLoading(true);
    dashboardService.getOwnerOverview(buildDateParams(dateRange, customStart, customEnd))
      .then((res) => {
        if (!cancelled) setData(res.data || null);
      })
      .catch((err) => {
        if (!cancelled) Alert.error(err.response?.data?.error || 'Could not load the owner dashboard');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [dateRange, customStart, customEnd]);

  const showGst = Number(data?.gst) > 0;
  const showTds = Number(data?.tds) > 0;
  const ownerCardCount = 5 + (Number(data?.commission) > 0 ? 1 : 0) + (showGst || showTds ? 1 : 0);
  const ownerGridClass = {
    5: 'grid-cols-2 md:grid-cols-3 xl:grid-cols-5',
    6: 'grid-cols-2 md:grid-cols-3 xl:grid-cols-6',
    7: 'grid-cols-2 md:grid-cols-4 xl:grid-cols-7',
  }[ownerCardCount] || 'grid-cols-2 md:grid-cols-3 xl:grid-cols-4';

  return (
    <DashboardLayout>
      <PageHeader title={`Welcome, ${user?.name || 'Owner'}`}>
        <DateRangeFilter
          value={dateRange}
          onChange={setDateRange}
          customStart={customStart}
          customEnd={customEnd}
          onCustomStartChange={setCustomStart}
          onCustomEndChange={setCustomEnd}
        />
      </PageHeader>

      {loading && !data ? <Loader fullScreen={false} message="Loading owner overview..." /> : null}

      {data ? (
        <>
          <div className={`grid gap-3 mb-4 ${ownerGridClass}`}>
            <StatCard
              label="Shipments"
              value={data.shipments || 0}
              icon={<Package className="w-5 h-5 text-white" />}
              color="bg-green-600"
            />
            <AmountCard
              label="Total revenue"
              amount={data.revenue}
              hint={`${data.paid_count || 0} paid · ${data.to_pay_count || 0} to-pay · ${data.credit_count || 0} credit`}
              icon={<IndianRupee className="w-5 h-5 text-white" />}
              color="bg-purple-500"
              subAmount={data.paid_total}
              subLabel="Paid"
              subAmount2={data.to_pay_total}
              subLabel2="To-Pay"
            />
            <AmountCard
              label="Expenses"
              amount={data.expenses}
              icon={<IndianRupee className="w-5 h-5 text-white" />}
              color="bg-rose-600"
              onClick={() => navigate('/expenses')}
            />
            <AmountCard
              label="Handling"
              amount={data.handling}
              icon={<PackageOpen className="w-5 h-5 text-white" />}
              color="bg-sky-600"
              subAmount={data.loading}
              subLabel="Loading"
              subAmount2={data.unloading}
              subLabel2="Unloading"
            />
            {Number(data.commission) > 0 ? (
              <AmountCard
                label="Commission"
                amount={data.commission}
                icon={<Percent className="w-5 h-5 text-white" />}
                color="bg-teal-600"
                subAmount={data.booking_commission}
                subLabel="Booking"
                subAmount2={data.delivery_commission}
                subLabel2="Delivery"
                onClick={() => navigate('/agent-commission-breakdown')}
              />
            ) : null}
            {showGst || showTds ? (
              <AmountCard
                label={showGst && showTds ? 'GST & TDS' : (showGst ? 'GST' : 'TDS')}
                amount={Number(data.gst || 0) + Number(data.tds || 0)}
                icon={<Landmark className="w-5 h-5 text-white" />}
                color="bg-slate-600"
                subAmount={showGst && showTds ? data.gst : undefined}
                subLabel={showGst && showTds ? 'GST' : undefined}
                subAmount2={showGst && showTds ? data.tds : undefined}
                subLabel2={showGst && showTds ? 'TDS' : undefined}
              />
            ) : null}
            <AmountCard
              label="Actual revenue"
              amount={data.actual_revenue}
              icon={<TrendingUp className="w-5 h-5 text-white" />}
              color="bg-emerald-600"
            />
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <div className="bg-white rounded-xl border border-gray-100 p-3">
              <p className="text-xs text-gray-500">Recovered from agents</p>
              <p className="mt-1 text-lg font-semibold text-emerald-800">{money(data.recovered_from_agents)}</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-3">
              <p className="text-xs text-gray-500">Still due from agents</p>
              <p className="mt-1 text-lg font-semibold text-amber-800">{money(data.due_from_agents)}</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-3">
              <p className="text-xs text-gray-500">Recovered from credit clients</p>
              <p className="mt-1 text-lg font-semibold text-emerald-800">{money(data.recovered_from_clients)}</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-3">
              <p className="text-xs text-gray-500">Still due from credit clients</p>
              <p className="mt-1 text-lg font-semibold text-amber-800">{money(data.due_from_clients)}</p>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-100 shadow-sm mb-6 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100">
              <h2 className="text-sm font-semibold text-gray-800">Agents</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[980px] text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="text-left px-3 py-2">Agent</th>
                    <th className="text-right px-3 py-2">Paid</th>
                    <th className="text-right px-3 py-2">Booking commission</th>
                    <th className="text-right px-3 py-2">Loading</th>
                    <th className="text-right px-3 py-2">To-Pay</th>
                    <th className="text-right px-3 py-2">Delivery commission</th>
                    <th className="text-right px-3 py-2">Unloading</th>
                    <th className="text-right px-3 py-2">Returned</th>
                    <th className="text-right px-3 py-2">Still due</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {(data.agents || []).length ? data.agents.map((row) => (
                    <tr
                      key={row.user_id}
                      onClick={() => setOpen({ kind: 'agent', userId: row.user_id })}
                      className="cursor-pointer hover:bg-sky-50/70"
                    >
                      <td className="px-3 py-2 font-medium text-gray-900">{row.name}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{money(row.paid_total)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{money(row.booking_commission)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{money(row.loading)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{money(row.topay_total)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{money(row.delivery_commission)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{money(row.unloading)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-emerald-800">{money(row.returned)}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-medium">{money(row.still_due)}</td>
                    </tr>
                  )) : (
                    <tr><td colSpan={9} className="px-3 py-8 text-center text-gray-400">No agent rows</td></tr>
                  )}
                </tbody>
                {(data.agents || []).length ? (
                  <tfoot>
                    <tr className="bg-gray-50 font-semibold text-gray-900">
                      <td className="px-3 py-2">Total</td>
                      <td className="px-3 py-2 text-right tabular-nums">{money(sumRows(data.agents, 'paid_total'))}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{money(sumRows(data.agents, 'booking_commission'))}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{money(sumRows(data.agents, 'loading'))}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{money(sumRows(data.agents, 'topay_total'))}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{money(sumRows(data.agents, 'delivery_commission'))}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{money(sumRows(data.agents, 'unloading'))}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-emerald-800">{money(sumRows(data.agents, 'returned'))}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{money(sumRows(data.agents, 'still_due'))}</td>
                    </tr>
                  </tfoot>
                ) : null}
              </table>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100">
              <h2 className="text-sm font-semibold text-gray-800">Credit clients</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="text-left px-3 py-2">Client</th>
                    <th className="text-left px-3 py-2">Company</th>
                    <th className="text-right px-3 py-2">Billed</th>
                    <th className="text-right px-3 py-2">Recovered</th>
                    <th className="text-right px-3 py-2">Outstanding</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {(data.credit_clients || []).length ? data.credit_clients.map((row) => (
                    <tr
                      key={row.client_id}
                      onClick={() => setOpen({ kind: 'client', clientId: row.client_id })}
                      className="cursor-pointer hover:bg-sky-50/70"
                    >
                      <td className="px-3 py-2 font-medium text-gray-900">{row.name}</td>
                      <td className="px-3 py-2 text-gray-600">{row.company_name || '—'}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{money(row.billed)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-emerald-800">{money(row.recovered)}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-medium">{money(row.outstanding)}</td>
                    </tr>
                  )) : (
                    <tr><td colSpan={5} className="px-3 py-8 text-center text-gray-400">No credit clients</td></tr>
                  )}
                </tbody>
                {(data.credit_clients || []).length ? (
                  <tfoot>
                    <tr className="bg-gray-50 font-semibold text-gray-900">
                      <td className="px-3 py-2" colSpan={2}>Total</td>
                      <td className="px-3 py-2 text-right tabular-nums">{money(sumRows(data.credit_clients, 'billed'))}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-emerald-800">{money(sumRows(data.credit_clients, 'recovered'))}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{money(sumRows(data.credit_clients, 'outstanding'))}</td>
                    </tr>
                  </tfoot>
                ) : null}
              </table>
            </div>
          </div>
        </>
      ) : null}
      {open ? (
        <ReturnBreakdownModal
          kind={open.kind}
          userId={open.userId}
          clientId={open.clientId}
          dateRange={dateRange}
          customStart={customStart}
          customEnd={customEnd}
          onClose={() => setOpen(null)}
        />
      ) : null}
    </DashboardLayout>
  );
};

export default OwnerDashboard;
